import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { LanguageCodeType } from "const/languageCodes";
import apiBaseUrl from "const/properties";
import { UserState } from "services/authService";
import TokenService from "services/tokenService";
import {
  CurrencyType,
  RemainingSeat,
  type CostParams,
  type CostResponse,
  type SeatMapParams,
  type SeatMapResult,
  type TrainResponse,
} from "services/trainService";
import { Criteria, TravelGood } from "services/travelService";
import { withPaging } from "types/utilityTypes";
import { logout } from "./actionCreators";
import { OrderType } from "types/orderType";
import { Coupon } from "utils/reservationUtils";
import { ScheduleType } from "types/scheduleType";
export type { TrainResponse };

type UpdatedUserInfo = Partial<{
  userId: number;
  name: string;
  phone: string;
  email: string;
  userStatus: string; // "ACTIVE" | "INACTIVE"
  isReceivingEmail: boolean;
  isReceivingSms: boolean;
  isReceivingTalk: boolean;
  oldPw: string;
  newPw: string;
  language: LanguageCodeType;
  updateDate: string;
}>;

export interface ActivityReview {
  reviewId: number;
  userId: number;
  userName: string;
  goodsId: number;
  rating: number;
  content: "string";
  createdDate: string; // YYYY-MM-DDTHH:mm:ss
  updatedDate: string; // YYYY-MM-DDTHH:mm:ss
  isVisible: true;
}

export interface ActivityInquiry {
  content: string;
  createdDate: string; // YYYY-MM-DDTHH:mm:ss
  goodsId: number;
  questionId: number;
  repliedDate: string; // YYYY-MM-DDTHH:mm:ss
  replyContent: string;
  status: "COMPLETE";
  title: string;
  updatedDate: string; // YYYY-MM-DDTHH:mm:ss
  userId: number;
  userName: string;
}

export interface Credential {
  [key: string]: Credential[keyof Credential];
  loginId: string;
  loginPassword: string;
  remember?: boolean;
}

export interface ActivitySchedule {
  [date: string]: {
    [optionCode: string]: number;
  };
}

interface HanpassPaymentHistory {
  payAmount: number;
  payCancelType: "CANCEL" | "PAY";
  paymentMerchantTransactionId: string;
  paymentMerchantTransactionId2: string;
  regDate: string;
  status: "CANCEL" | "COMPLETE";
}

export interface HanpassReceipt {
  // appTxnPaySeq: number;
  payAmount: number;
  payCurrency: "KRW";
  payHistory: HanpassPaymentHistory[];
  paymentMerchantCode: string;
  paymentMerchantTransactionId: string | null;
  status: "CANCEL" | "COMPLETE";
  orderName: string;
  // transactionDate: string;
}

type pagingParams<T> = T & {
  page: number;
  size: number;
};

const baseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  // credentials: 'include'로 설정하여 쿠키가 자동으로 전송되도록 함
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = TokenService.getAccessToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// 토큰 갱신 상태 관리를 위한 변수들
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // console.log("RTK query result", result);

  if (
    result.error?.status === 401 &&
    (result.error.data as { errorMessage: string }).errorMessage ===
      "ACCESS_TOKEN_EXPIRED"
  ) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = (async () => {
        try {
          // refreshToken은 쿠키에 있으므로 body 없이 요청
          const refreshResult = await baseQuery(
            {
              url: "/user/refresh",
              method: "POST",
              // credentials가 이미 baseQuery에 설정되어 있으므로 쿠키가 자동으로 포함됨
            },
            api,
            extraOptions
          );

          if (refreshResult.data) {
            // 새로운 accessToken만 저장
            const { accessToken } = refreshResult.data as {
              accessToken: string;
            };

            TokenService.setAccessToken(accessToken);
          } else {
            // 리프레시 토큰도 만료된 경우
            throw new Error("Refresh token expired");
          }
        } catch (error) {
          TokenService.removeAccessToken();
          // Reset RTK cache and Redux store
          const dispatch = api.dispatch;
          dispatch(apiSlice.util.resetApiState());
          dispatch(logout());
          window.location.href = "/login";
        } finally {
          isRefreshing = false;
          refreshPromise = null;
        }
      })();
    }

    // 토큰 갱신이 진행 중이라면 완료될 때까지 대기
    if (refreshPromise) {
      await refreshPromise;
      // 토큰 갱신 후 원래 요청 재시도
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

// Define a service using a base URL and expected endpoints
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: "api",
  // baseQuery: fetchBaseQuery({ baseUrl: apiBaseUrl }),
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "TrainStation",
    "RecommendedActivity",
    "MyInfo",
    "Point",
    "Inquiry",
    "Review",
  ],

  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    // The `getStations` endpoint is a "query" operation that returns data.
    // The return value is a `TrainResponse[]` array, and it takes no arguments.

    // 역 목록 조회
    getStations: builder.query<TrainResponse[], void>({
      query: () => "/train/stations",
      keepUnusedDataFor: 3600, // 캐시 유지 시간을 1시간으로 설정
    }),

    // jtr 상품 목록 조회
    getActivityList: builder.query<withPaging<TravelGood>, Criteria>({
      query: (criteria) => ({
        url: `/goods/list/${criteria.category}`,
        params: { size: 10, ...criteria }, // 기본 size = 10
      }),
    }),

    // jtr 추천 상품 조회
    getRecommendedActivity: builder.query<withPaging<TravelGood>, void>({
      query: () => ({ url: "/goods/list", params: { recommend: true } }),
    }),

    // jtr 개별 상품 조회
    getActivity: builder.query<TravelGood, string>({
      query: (goodsId) => ({
        url: "/goods/moreDetail",
        params: {
          goodsId,
        },
      }),
      keepUnusedDataFor: 3600, // 캐시 유지 시간을 1시간으로 설정
    }),

    // jtr 상품 리뷰 조회
    activityReviews: builder.query<
      withPaging<ActivityReview>,
      pagingParams<{ goodsId: number }>
    >({
      query: ({ goodsId, ...params }) => ({
        url: `/goods/reviews/${goodsId}`,
        params,
      }),
      providesTags: ["Review"],
    }),

    // jtr 상품 리뷰 작성 가능 여부
    activityReviewWriteable: builder.query<
      { isWriteable: 0 | 1 }, // 0: 작성 불가, 1: 작성 가능
      { goodsId: number }
    >({
      query: ({ goodsId }) => ({
        url: `/goods/review/check/${goodsId}`,
        method: "POST",
      }),
      providesTags: ["Review"],
    }),
    // jtr 상품 리뷰 작성
    makeActivityReview: builder.mutation<
      string,
      {
        goodsId: number;
        rating: number;
        content: string;
      }
    >({
      query: (review) => ({
        url: "/goods/review/write",
        method: "POST",
        body: review,
        responseHandler: "text", // text response 처리
      }),
      invalidatesTags: ["Inquiry"],
    }),

    // jtr 상품 문의 조회
    activityInquiries: builder.query<
      withPaging<ActivityInquiry>,
      pagingParams<{ goodsId: number }>
    >({
      query: ({ goodsId, ...params }) => ({
        url: `/goods/questions/${goodsId}`,
        params,
      }),
      providesTags: ["Inquiry"],
    }),

    // jtr 웗변 선택 가능 일정 정보
    activitySchedule: builder.query<
      ActivitySchedule,
      {
        goodsId: number | string;
        startDate: string;
      }
    >({
      query: (params) => ({ url: "/goods/dailyPrices", params }),
    }),

    makeActivityInquiry: builder.mutation<
      string,
      {
        goodsId: number;
        title: string;
        content: string;
      }
    >({
      query: (inquiry) => ({
        url: "/goods/question/write",
        method: "POST",
        body: inquiry,
        responseHandler: "text", // text response 처리
      }),
      invalidatesTags: ["Inquiry"],
    }),

    updateActivityInquiry: builder.mutation<
      string,
      {
        questionId: number;
        title: string;
        content: string;
      }
    >({
      query: (inquiry) => ({
        url: "/goods/question/modify",
        method: "POST",
        body: inquiry,
        responseHandler: "text", // text response 처리
      }),
      invalidatesTags: ["Inquiry"],
    }),

    // user api
    // 사용자 정보 조회
    myInfo: builder.query<UserState, void>({
      query: () => "/user/myInfo",
      providesTags: ["MyInfo"],
    }),

    MyInfo: builder.mutation<string, UpdatedUserInfo>({
      query: (updatedUserInfo) => ({
        url: "/user/update",
        method: "POST",
        body: updatedUserInfo,
        responseHandler: "text", // text response 처리
      }),
      invalidatesTags: ["MyInfo", "Point"],
    }),

    // 포인트 조회
    getMyPoint: builder.query<number, void>({
      query: () => "/user/myPoint",
      providesTags: ["Point"],
      keepUnusedDataFor: 0,
    }),

    getOrderListOfPayment: builder.query<OrderType, number>({
      query: (paymentId) => ({
        url: `/user/payment`,
        params: { paymentId },
      }),
    }),

    // 포인트 적립/사용 내역
    // 무한 스크롤 적용되는 부분으로 axios 사용
    // getPointHistory: builder.query<
    //   PointHistories,
    //   { page: number; size: number }
    // >({
    //   query: ({ page = 0, size = 10 }) => ({
    //     url: "/user/myPointEvent/history",
    //     params: { page, size },
    //   }),
    //   providesTags: ["Point"],
    // }),

    // 결제/환불 api 에는 invalidatesTags: ["Point"] 추가

    // 회원가입

    register: builder.mutation<{ accessToken: string }, FormData>({
      query: (userData) => ({
        url: "/user/signup",
        method: "POST",
        body: userData,
      }),
      // transformResponse: (response: { data: any }, meta, arg) => response.data,
    }),

    // 로그인
    login: builder.mutation<{ accessToken: string }, Credential>({
      query: (credentials) => {
        const formData = new FormData();
        formData.append("loginId", credentials.loginId);
        formData.append("password", credentials.loginPassword);
        return {
          url: "/user/login",
          method: "POST",
          body: formData,
          credentials: "include",
        };
      },
      // invalidatesTags: ["MyInfo", "Point"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const result = await queryFulfilled; // 요청 성공
          TokenService.setAccessToken(result.data.accessToken); // 토큰 저장
        } catch (error) {
          console.error("Login Failed:", error);
          // alert("Login error");
          alert("아이디 또는 비밀번호가 맞지 않습니다.");
        }
      },
    }),

    // 하나카드 로그인
    hanaLogin: builder.mutation<
      { accessToken: string },
      { cno: string; ts: string; link: string }
    >({
      query: ({ cno, ts, link }) => ({
        url: "/hanacard/login",
        method: "POST",
        params: {
          cno,
          ts,
          link,
        },
      }),
    }),

    // 로그아웃
    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/user/logout",
        method: "POST",
      }),
      invalidatesTags: ["MyInfo", "Point"],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled; // 요청 성공
          TokenService.removeAccessToken(); // 토큰 삭제
          // delete fetchBaseQuery.headers?.Authorization; // 글로벌 헤더에서 제거
        } catch (error) {
          console.error("Logout Failed:", error);
          alert("Logout error");
          throw error;
        }
      },
    }),

    // 기차 검색 결과 조회 - 무한 스크롤 적용되어야 함
    // getSearchResult: builder.query<SearchResult, SearchResultParams>({
    //   query: (params) => ({
    //     url: "/train/searchResult",
    //     params,
    //   }),
    // }),

    // 열차 요금 조회
    getSearchCost: builder.query<CostResponse[], CostParams>({
      query: (params) => ({
        url: "/train/searchCost",
        params,
      }),
    }),

    // 좌석 배치도 조회
    getSeatMap: builder.query<SeatMapResult, SeatMapParams>({
      query: (params) => ({
        url: "/train/seatMap",
        params,
      }),
    }),

    // 남은 좌석 조회
    getSearchRemainingSeats: builder.query<RemainingSeat[], SeatMapParams>({
      query: (params) => ({
        url: "/train/searchRestSeat",
        params,
      }),
    }),

    // 기차 결합상품 조회
    getRelatedGoods: builder.query<
      Coupon[],
      {
        stationCode: ScheduleType["arvRsStnCd"];
      }
    >({
      query: ({ stationCode }) => ({
        url: "/train/relatedGoods",
        params: { stationCode },
      }),
    }),

    // 기차표 가격 재조회 (복합 상품 변경 시 요청)
    getTicketPrice: builder.query<OrderType, FormData>({
      query: (data) => ({
        url: "/train/ticketPriceCheck",
        method: "POST",
        body: data,
        formData: true,
      }),
    }),

    // 기차표 예약
    reserveTicket: builder.mutation<OrderType, FormData>({
      query: (data) => ({
        url: "/train/ticketReserve",
        method: "POST",
        body: data,
        formData: true,
      }),
    }),

    // 환율 조회
    getCurrency: builder.query<{ rate: number }, CurrencyType>({
      query: (code) => ({
        url: "/train/currency",
        params: { code },
      }),
    }),

    // 한패스 월렛 영수증 조회
    getHanpassReceipt: builder.query<HanpassReceipt, string>({
      query: (paymentId) => ({
        url: "/train/receipt/hanpass",
        params: { paymentId },
      }),
    }),

    // 한패스 쿠폰 할인금액 조회
    getHanpassCouponDiscountAmount: builder.query<
      { data: number },
      { orderId: number; externalCouponId: number }
    >({
      query: ({ orderId, externalCouponId }) => ({
        url: "/train/hanpassCouponCheck",
        params: { orderId, externalCouponId },
      }),
    }),
  }),
});

// Export the auto-generated hook based on the defined endpoints
export const {
  useGetStationsQuery,

  useGetActivityListQuery,
  useGetRecommendedActivityQuery,
  useGetActivityQuery,
  useActivityScheduleQuery,
  useActivityReviewsQuery,
  useActivityReviewWriteableQuery,
  useMakeActivityReviewMutation,
  useActivityInquiriesQuery,
  useMakeActivityInquiryMutation,
  useUpdateActivityInquiryMutation,

  useMyInfoQuery,
  useLazyMyInfoQuery,
  useMyInfoMutation,
  useGetMyPointQuery,
  useGetOrderListOfPaymentQuery,

  useRegisterMutation,
  useLoginMutation,
  useHanaLoginMutation,
  useLogoutMutation,

  // useGetSearchResultQuery,
  useGetSearchCostQuery,
  useLazyGetSearchCostQuery,
  useLazyGetSeatMapQuery, // useLazyQuery
  useLazyGetSearchRemainingSeatsQuery,
  useGetRelatedGoodsQuery,
  useGetTicketPriceQuery,
  useLazyGetTicketPriceQuery,
  useReserveTicketMutation,

  // 환율
  useGetCurrencyQuery,

  // 한패스
  useGetHanpassReceiptQuery,
  useLazyGetHanpassCouponDiscountAmountQuery,
} = apiSlice;
